import React, {Component} from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Validator from '../../helpers/validator';
import {PaymentAction} from '../../actions';
import {connect} from 'react-redux';
import Utility from "../../helpers/Utility";
import NotFound from "../NotFound";
import Animate from "../Animate";
import { ThemeContext } from '../../ThemeContext';
import { Player } from "@lottiefiles/react-lottie-player";
import successful from "../../assets/raw/successful.json";
import failed from "../../assets/raw/payment-failed.json";
import locked from "../../assets/raw/locked.json";
import PaymentStatus from "../layout/PaymentStatus";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

/* ── Shared SVG icons ── */
const LockIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
);
const BankIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="22" x2="21" y2="22"/>
        <line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/>
        <line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/>
        <polygon points="12 2 20 7 4 7"/>
    </svg>
);
const CardIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
);
const UssdIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
        <line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/>
    </svg>
);
const ShieldIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
);
const CopyIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
);

/* ── Stripe inner form ── */
const StripePaymentForm = ({ amount, currency, onSuccess, onError, onProcessing }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = React.useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setIsProcessing(true);
        onProcessing(true);
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });
        setIsProcessing(false);
        onProcessing(false);
        if (error) onError(error.message || 'Payment failed');
        else if (paymentIntent?.status === 'succeeded') onSuccess();
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="ps-form-group">
                <label className="ps-label">Amount</label>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                    <span className="ps-input-addon-token">{currency.toUpperCase()}</span>
                    <input className="ps-input ps-input-attached" value={Utility.numberFormat(amount || 0, 2)} readOnly />
                </div>
            </div>
            <div className="ps-form-group">
                <label className="ps-label">Payment Information</label>
                <div className="ps-stripe-wrapper"><PaymentElement /></div>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <LockIcon /> Secured by Stripe
                </p>
            </div>
            <button type="submit" disabled={!stripe || isProcessing} className="ps-btn-primary">
                {isProcessing ? <span className="ps-spinner" /> : (
                    <><LockIcon /><span>Pay {Utility.moneyFormat(amount || 0, 2, 'currency', currency)}</span></>
                )}
            </button>
        </form>
    );
};

class Home extends Component {
    static contextType = ThemeContext;
    state = {
        error: { firstname: '', lastname: '', email: '' },
        channel: 'bank_transfer',
        copiedAccountNumber: null,
        stripeClientSecret: null,
        stripeProcessing: false,
        showStripeElement: false,
        alertMessage: null,
        alertVariant: null,
        showAlert: false,
        alertExiting: false,
    };

    componentDidMount() {
        const { dispatch, match: { params: { id } } } = this.props;
        dispatch(PaymentAction.fetchPaymentData(id));
    }

    componentDidUpdate(prevProps) {
        const { paymentLink } = this.props;
        const prevPaymentLink = prevProps?.paymentLink;
        if (paymentLink?.data !== prevPaymentLink?.data) {
            const paymentLinkData = paymentLink?.data;
            if (paymentLinkData?.message && !paymentLink?.requesting) {
                // Only show the top-level alert when there are no field-level errors to display inline
                if (!paymentLinkData?.errors || Object.keys(paymentLinkData.errors).length === 0) {
                    this.showAlert(paymentLinkData.message, paymentLinkData?.status ? 'success' : 'danger');
                }
            }
            if (paymentLinkData?.status === false && paymentLinkData?.errors) {
                // Spread server field errors directly into component error state so they
                // appear inline under their respective inputs
                this.setState(prev => ({ error: { ...prev.error, ...paymentLinkData.errors } }));
            }
            if (paymentLinkData?.currency !== 'NGN' && paymentLinkData?.data?.client_secret && paymentLinkData?.status === true) {
                this.setState({ stripeClientSecret: paymentLinkData.data.client_secret, showStripeElement: true, stripeProcessing: false });
            }
            if (paymentLinkData?.status === false) {
                this.setState({ showStripeElement: false, stripeClientSecret: null, stripeProcessing: false });
            }
        }
    }

    showAlert = (message, variant = 'danger') => {
        this.setState({ alertMessage: message, alertVariant: variant, showAlert: true, alertExiting: false });
        setTimeout(() => this.dismissAlert(), 5000);
    };
    dismissAlert = () => {
        this.setState({ alertExiting: true });
        setTimeout(() => this.setState({ showAlert: false, alertExiting: false }), 200);
    };
    hideAlert = () => this.dismissAlert();

    handleCopy = (accountNumber) => {
        navigator.clipboard.writeText(accountNumber).then(() => {
            this.setState({ copiedAccountNumber: accountNumber });
            setTimeout(() => this.setState({ copiedAccountNumber: null }), 2000);
        }).catch(err => console.error(err));
    };

    submit = (e) => {
        e.preventDefault();
        const validator = new Validator(e);
        validator.validate('firstname').isUsername("Please enter a valid first name");
        validator.validate('lastname').isUsername("Please enter a valid last name");
        validator.validate('email').isEmail("Please enter a valid email address");

        if (validator.hasError()) {
            this.setState({ error: { ...validator.getErrorsFlat() } });
        } else {
            this.setState({ error: { firstname: '', lastname: '', email: '' }, stripeClientSecret: null, showStripeElement: false, showAlert: false });
            const { dispatch, match: { params: { id } } } = this.props;
            const name = `${validator.getValue('firstname')} ${validator.getValue('lastname')}`;
            dispatch(PaymentAction.generatePaymentLink(id, name, validator.getValue('email'), validator.getValue('channel')));
        }
    };

    handleStripeSuccess = () => { window.location.href = `${window.location.pathname}?status=success`; };
    handleStripeError  = (err) => { this.showAlert(err, 'danger'); this.setState({ showStripeElement: false, stripeClientSecret: null }); };
    handleStripeProcessing = (v) => this.setState({ stripeProcessing: v });

    render() {
        const { isDark } = this.context;
        const { error, channel, copiedAccountNumber, stripeClientSecret, stripeProcessing, showStripeElement, alertMessage, alertVariant, showAlert, alertExiting } = this.state;
        const { paymentData, paymentLink, location } = this.props;

        const stripeAppearance = {
            theme: isDark ? 'night' : 'stripe',
            variables: {
                colorPrimary: '#015ad9',
                colorBackground: isDark ? '#1e293b' : '#ffffff',
                colorText: isDark ? '#f1f5f9' : '#1e293b',
                colorDanger: '#ef4444',
                fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                borderRadius: '12px',
                spacingUnit: '4px',
            },
        };

        const paymentLinkData = paymentLink?.data;
        const payment = paymentData?.data?.data?.payment;
        const isNGN = payment?.currency === 'NGN';

        if (paymentData.requesting || !('status' in (paymentData.data || {}))) {
            // Network / parse error — data has no `status` key but we are no longer requesting
            if (!paymentData.requesting && paymentData.data?.message) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', maxWidth: 360, margin: 0 }}>
                            {paymentData.data.message}
                        </p>
                        <button className="ps-btn-primary" style={{ maxWidth: 200 }}
                            onClick={() => {
                                const { dispatch, match: { params: { id } } } = this.props;
                                dispatch(PaymentAction.fetchPaymentData(id));
                            }}>
                            Try Again
                        </button>
                    </div>
                );
            }
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                    <span className="ps-spinner ps-spinner-dark" style={{ width: 32, height: 32 }} />
                </div>
            );
        }

        if (paymentData && paymentData?.data?.status === false) return <NotFound message={paymentData?.data?.message}/>;
        if (payment && payment?.direction !== 'pay-in') return <NotFound message="Invalid payment link"/>;

        if (payment?.is_paid && !payment?.is_reoccurring) {
            return (
                <div className="ps-card" style={{ maxWidth: 480, margin: '2rem auto', width: '100%' }}>
                    <div className="ps-card-body ps-status-screen">
                        <Player autoplay loop src={locked} style={{ height: 130, width: 130 }} />
                        <h4 className="ps-status-title" style={{ color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                            Payment Already Made
                        </h4>
                        <p className="ps-status-subtitle">
                            This payment link has already been used. Please reach out to the merchant for a new payment link or make this a reoccurring payment.
                        </p>
                    </div>
                </div>
            );
        }
        if (isNGN && paymentLinkData?.status && channel === 'card' && paymentLinkData?.data?.authorization_url) window.location.href = paymentLinkData?.data?.authorization_url;

        const query = new URLSearchParams(location.search);
        const statusParam = query.get('status');
        // Only honour ?status when the server previously confirmed a payment link was generated
        const serverConfirmed = paymentLinkData?.status === true || paymentLinkData?.status === false;
        const status = (statusParam && serverConfirmed)
            ? (['success', 'successful'].includes(statusParam) ? 'successful' : 'failed')
            : undefined;
        const firstname = query.get('firstname');
        const lastname  = query.get('lastname');
        const email     = query.get('email');
        const showStripe = !isNGN && showStripeElement && stripeClientSecret && paymentLinkData?.status === true;
        const showNGNBankDetails = isNGN && paymentLinkData?.status && channel === 'bank_transfer';

        /* ── Payment status redirect screen ── */
        if (status) {
            return (
                <div className="ps-card" style={{ maxWidth: 480, margin: '2rem auto', width: '100%' }}>
                    <div className="ps-card-body ps-status-screen">
                        <Player autoplay loop src={status === 'successful' ? successful : failed} style={{ height: 130, width: 130 }} />
                        <h4 className={`ps-status-title ${status === 'successful' ? 'success' : 'failed'}`} style={{ marginTop: '0.75rem' }}>
                            Payment {status === 'successful' ? 'Successful' : 'Failed'}
                        </h4>
                        <p className="ps-status-subtitle">
                            {status === 'successful' ? 'Your payment has been processed successfully.' : 'We could not complete your payment. Please try again.'}
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div style={{ width: '100%' }}>
                {/* Page header */}
                <div className="ps-page-header">
                    <span className="ps-badge"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline",verticalAlign:"middle",marginRight:"4px",marginBottom:"1px"}}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>SECURE PAYMENT</span>
                    <p className="ps-page-title">
                        Make payment to{' '}
                        {paymentData.requesting
                            ? <span className="ps-spinner ps-spinner-dark" style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: 'middle' }} />
                            : <strong>{payment?.company?.name || payment?.user?.name}</strong>}
                    </p>
                </div>

                <div className="ps-checkout-grid">
                    {/* ── Left: summary ── */}
                    <div className="ps-info-stack">
                        <div className="ps-card">
                            <div className="ps-card-body">
                                <div className="ps-card-header">
                                    <div className="ps-card-header-left">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                                            <line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/>
                                        </svg>
                                        Payment Summary
                                    </div>
                                </div>
                                {paymentData.requesting ? (
                                    <div style={{ marginTop: '1rem' }}>
                                        <div className="ps-shimmer" style={{ width: '70%', marginBottom: '0.5rem' }} />
                                        <div className="ps-shimmer ps-shimmer-sm" />
                                    </div>
                                ) : (
                                    <div style={{ paddingTop: '0.25rem' }}>
                                        <p className="ps-payment-detail">{payment?.title}</p>
                                        <p className="ps-payment-desc">{payment?.description}</p>
                                        <div className="ps-amount-row">
                                            <span className="ps-amount-label">Amount to Pay</span>
                                            <span className="ps-amount-value">
                                                {Utility.moneyFormat(payment?.amount || 0, 2, 'currency', payment?.currency)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="ps-trust-badge">
                            <ShieldIcon />
                            <div>
                                <h4>256-Bit SSL Secured</h4>
                                <p>Your payment information is encrypted and secure.</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Right: payment panel ── */}
                    <div className="ps-payment-panel">
                        <div className="ps-payment-inner">
                            {/* Method tabs */}
                            <div className="ps-method-selector">
                                <span className="ps-method-label">Payment Methods</span>
                                <button type="button"
                                    className={`ps-method-btn ${channel === 'bank_transfer' ? 'active' : ''}`}
                                    onClick={() => { this.props.dispatch(PaymentAction.resetPaymentLink()); this.setState({ channel: 'bank_transfer', showStripeElement: false, stripeClientSecret: null, showAlert: false }); }}>
                                    <BankIcon /> Bank Transfer
                                </button>
                                <button type="button"
                                    className={`ps-method-btn ${channel === 'card' ? 'active' : ''}`}
                                    onClick={() => { this.props.dispatch(PaymentAction.resetPaymentLink()); this.setState({ channel: 'card', showStripeElement: false, stripeClientSecret: null, showAlert: false }); }}>
                                    <CardIcon /> Card Payment
                                </button>
                                <button type="button" className="ps-method-btn" disabled title="Coming soon">
                                    <UssdIcon /> USSD Payment
                                </button>
                            </div>

                            {/* Form panel */}
                            <div className="ps-form-panel">
                                {/* Alert */}
                                {showAlert && alertMessage && !showStripe && (
                                    <Animate
                                        animKey={alertExiting ? 'out' : 'in'}
                                        enter="ps-anim-slide-in-down"
                                        exit="ps-anim-slide-out-up"
                                        duration={200}
                                    >
                                        <div className={`ps-alert ${alertVariant === 'success' ? 'ps-alert-success' : 'ps-alert-danger'}`}>
                                            <span style={{ flex: 1 }}>{alertMessage}</span>
                                            <button className="ps-alert-close" onClick={this.hideAlert}>×</button>
                                        </div>
                                    </Animate>
                                )}

                                <Animate
                                    animKey={`${channel}-${showNGNBankDetails}-${showStripe}`}
                                    enter="ps-anim-slide-in-right"
                                    exit="ps-anim-slide-out-left"
                                    duration={200}
                                    style={{ flex: 1 }}
                                >

                                {/* NGN bank transfer details */}
                                {showNGNBankDetails ? (
                                    <div>
                                        <p className="ps-section-title">Bank Account Details</p>
                                        <div className="ps-bank-details">
                                            <div className="ps-bank-detail-row">
                                                <span className="ps-bank-detail-key">Bank Name</span>
                                                <span className="ps-bank-detail-val">{paymentLinkData.data.bank_name}</span>
                                            </div>
                                            <div className="ps-bank-detail-row">
                                                <span className="ps-bank-detail-key">Account Name</span>
                                                <span className="ps-bank-detail-val">{paymentLinkData.data.account_name}</span>
                                            </div>
                                            <div className="ps-bank-detail-row">
                                                <span className="ps-bank-detail-key">Account Number</span>
                                                <span className="ps-bank-detail-val">
                                                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.06em' }}>
                                                        {paymentLinkData.data.account_number}
                                                    </span>
                                                    {copiedAccountNumber === paymentLinkData.data.account_number
                                                        ? <span className="ps-copied-badge">Copied!</span>
                                                        : <button type="button" className="ps-copy-btn" onClick={() => this.handleCopy(paymentLinkData.data.account_number)}><CopyIcon /></button>
                                                    }
                                                </span>
                                            </div>
                                            <div className="ps-bank-detail-row">
                                                <span className="ps-bank-detail-key">Payment Status</span>
                                                <span className="ps-bank-detail-val">
                                                    <PaymentStatus reference={paymentLinkData.data.reference} />
                                                </span>
                                            </div>
                                        </div>

                                        {/* Amount to pay — clean card, not a button */}
                                        <div className="ps-amount-card">
                                            <div className="ps-amount-card-label">Amount to Pay</div>
                                            <div className="ps-amount-card-value">
                                                {Utility.moneyFormat(paymentLinkData.data.amount, 2, 'currency', paymentLinkData.data.currency)}
                                            </div>
                                        </div>
                                    </div>
                                ) : showStripe ? (
                                    <Elements key={`stripe-${isDark}`} stripe={stripePromise} options={{ clientSecret: stripeClientSecret, appearance: stripeAppearance }}>
                                        <StripePaymentForm
                                            amount={payment?.amount || 0}
                                            currency={payment?.currency || 'usd'}
                                            onSuccess={this.handleStripeSuccess}
                                            onError={this.handleStripeError}
                                            onProcessing={this.handleStripeProcessing}
                                        />
                                    </Elements>
                                ) : (
                                    <form onSubmit={this.submit}>
                                        {/* Amount */}
                                        <div className="ps-form-group">
                                            <label className="ps-label">Amount</label>
                                            <div style={{ display: 'flex', alignItems: 'stretch' }}>
                                                <span className="ps-input-addon-token">
                                                    {paymentData.requesting
                                                        ? <span className="ps-spinner ps-spinner-dark" style={{ width: 14, height: 14 }} />
                                                        : payment?.currency}
                                                </span>
                                                <input className="ps-input ps-input-attached"
                                                    value={Utility.numberFormat(payment?.amount || 0)}
                                                    readOnly />
                                            </div>
                                        </div>

                                        {/* Name row */}
                                        <div className="ps-form-row">
                                            <div className="ps-form-group" style={{ marginBottom: 0 }}>
                                                <label className="ps-label" htmlFor="firstname">First Name</label>
                                                <input className={`ps-input${error.firstname ? ' is-invalid' : ''}`}
                                                    type="text" id="firstname" name="firstname"
                                                    placeholder="Enter first name" defaultValue={firstname || ''} />
                                                {error.firstname && <span className="ps-error-text">{error.firstname}</span>}
                                            </div>
                                            <div className="ps-form-group" style={{ marginBottom: 0 }}>
                                                <label className="ps-label" htmlFor="lastname">Last Name</label>
                                                <input className={`ps-input${error.lastname ? ' is-invalid' : ''}`}
                                                    type="text" id="lastname" name="lastname"
                                                    placeholder="Enter last name" defaultValue={lastname || ''} />
                                                {error.lastname && <span className="ps-error-text">{error.lastname}</span>}
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="ps-form-group" style={{ marginTop: '1rem' }}>
                                            <label className="ps-label" htmlFor="email">Email Address</label>
                                            <input className={`ps-input${error.email ? ' is-invalid' : ''}`}
                                                type="email" id="email" name="email"
                                                placeholder="Enter email address" defaultValue={email || ''} />
                                            {error.email && <span className="ps-error-text">{error.email}</span>}
                                        </div>

                                        <input type="hidden" name="channel" value={channel} />

                                        <button type="submit"
                                            disabled={paymentLink?.requesting || (!isNGN && !showStripeElement && stripeProcessing)}
                                            className="ps-btn-primary">
                                            {paymentLink?.requesting
                                                ? <span className="ps-spinner" />
                                                : <><LockIcon /><span>Proceed to Pay</span></>
                                            }
                                        </button>
                                    </form>
                                )}
                                </Animate>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default connect(state => ({
    paymentData: state.paymentData,
    paymentLink: state.paymentLink,
}))(Home);
